import type { JettonStatus } from '@/entities/jetton';

import type { IClock } from './clock';
import { RafClock } from './clock';
import type { IGrowthCurve } from './growthCurve';
import { ExponentialGrowthCurve } from './growthCurve';
import { type Listener, Subject, type Unsubscribe } from './observable';

export const BASE_MULTIPLIER = 1.0;

/** Inputs that fully determine the multiplier for a round phase. */
export interface RoundPhase {
  status: JettonStatus;
  startTimeMs: number | null;
  crashMultiplier: number | null;
}

/**
 * Owns the live multiplier: applies the round rules, drives the per-frame
 * computation through an injected clock, and publishes values to subscribers.
 *
 * Framework-agnostic by design — depends on IClock / IGrowthCurve / Subject
 * abstractions (DIP), so it can run and be tested without React or the DOM.
 */
export class MultiplierEngine {
  private readonly subject = new Subject<number>();
  private stopClock: Unsubscribe | null = null;
  private current = BASE_MULTIPLIER;

  constructor(
    private readonly curve: IGrowthCurve = new ExponentialGrowthCurve(),
    private readonly clock: IClock = new RafClock(),
  ) {}

  /** Latest computed multiplier (read on demand, e.g. in event handlers). */
  get value(): number {
    return this.current;
  }

  subscribe(listener: Listener<number>): Unsubscribe {
    const unsubscribe = this.subject.subscribe(listener);
    listener(this.current);
    return unsubscribe;
  }

  /** Apply a round phase: stops any running loop and starts the right one. */
  start({ status, startTimeMs, crashMultiplier }: RoundPhase): void {
    this.stop();

    if (status === 'crashed' && crashMultiplier != null) {
      this.set(crashMultiplier);
      return;
    }

    if (status !== 'flying' || startTimeMs == null) {
      this.set(BASE_MULTIPLIER);
      return;
    }

    this.stopClock = this.clock.onFrame(() => {
      this.set(this.curve.at(this.clock.now() - startTimeMs));
    });
  }

  /** Stop the frame loop without tearing down subscribers. */
  stop(): void {
    this.stopClock?.();
    this.stopClock = null;
  }

  /** Stop and release all subscribers. */
  dispose(): void {
    this.stop();
    this.subject.clear();
  }

  private set(value: number): void {
    this.current = value;
    this.subject.emit(value);
  }
}
